import React, { useState, useEffect } from 'react';
import { Star, MessageSquare, Send, LogIn, AlertCircle, Loader, Sparkles, Quote } from 'lucide-react';
import { Link } from 'react-router-dom';
import { API_BASE } from '../config';

export default function Reviews({ t, user }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newRating, setNewRating] = useState(5);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => { fetchReviews(); }, []);

  const fetchReviews = async () => {
    try {
      const res = await fetch(`${API_BASE}/reviews`);
      if (res.ok) setReviews(await res.json());
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await fetch(`${API_BASE}/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer_id: user.id, rating: newRating, comment: newComment })
      });
      if (res.ok) { setNewComment(''); setNewRating(5); fetchReviews(); } 
      else { const errorData = await res.json(); setSubmitError(errorData.error || t.booking.error); }
    } catch (err) { setSubmitError(t.booking.error); } finally { setSubmitting(false); }
  };

  const renderStars = (count) => [...Array(5)].map((_, i) => ( 
    <Star key={i} className={`h-4 w-4 ${i < count ? 'text-amber-400 fill-amber-400' : 'text-slate-300'}`} /> 
  ));

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-slate-50 to-white">
      {/* En-tête élégant */}
      <div className="relative py-12 px-4 bg-gradient-to-br from-slate-900 via-brand-900 to-slate-800 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yIDItNCAzLTRzMyAyIDMgNHYyYzAgMi0yIDQtMyA0cy0zLTItMy00di0yem0wLTMwYzAtMiAyLTQgMy00czMgMiAzIDR2MmMwIDItMiA0LTMgNHMtMy0yLTMtNFY0ek0wIDM0YzAtMiAyLTQgMy00czMgMiAzIDR2MmMwIDItMiA0LTMgNHMtMy0yLTMtNHYtMnptMC0zMGMwLTIgMi00IDMtNHMzIDIgMyA0djJjMCAyLTIgNC0zIDRzLTMtMi0zLTRWNHoiLz48L2c+PC9nPjwvc3ZnPg==')] animate-pulse"></div>
        </div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center justify-center p-1 mb-4 bg-white/10 backdrop-blur-xl rounded-full border border-white/30 px-4">
            <Sparkles className="h-3 w-3 mr-2 text-brand-300" />
            <span className="text-[9px] font-black uppercase tracking-[0.2em] text-brand-50">
              {t.reviews?.badge || 'Témoignages Authentiques'}
            </span>
          </div>
          
          <h2 className="text-3xl md:text-4xl font-black text-white mb-2 font-serif italic tracking-tight">
            {t.reviews?.title || 'Avis Clients'}
          </h2>
          <p className="text-base text-slate-200 font-medium opacity-90">
            {t.reviews?.subtitle || 'Découvrez ce que nos clients pensent de nous'}
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-16">
        {/* Formulaire d'ajout d'avis */}
        <div className="bg-white rounded-3xl shadow-xl p-8 md:p-10 border border-slate-200 mb-16 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-brand-50 rounded-full blur-3xl opacity-30 -mr-32 -mt-32"></div>
          
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-brand-100 rounded-2xl">
                <MessageSquare className="h-6 w-6 text-brand-700" />
              </div>
              <h3 className="text-2xl md:text-3xl font-black text-slate-900 font-serif italic">
                {t.reviews?.addTitle || "Partagez votre expérience"}
              </h3>
            </div>

            {user ? (
              <form onSubmit={handleSubmit} className="space-y-6">
                {submitError && (
                  <div className="p-4 bg-red-50 text-red-700 rounded-2xl text-sm flex items-center border border-red-200">
                    <AlertCircle className="h-5 w-5 mr-3 flex-shrink-0" />
                    {submitError}
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-black uppercase tracking-wider text-slate-700 mb-3">
                    {t.reviews?.ratingLabel || "Votre note"}
                  </label>
                  <div className="flex space-x-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button 
                        type="button" 
                        key={star} 
                        onClick={() => setNewRating(star)} 
                        className="focus:outline-none transition-all hover:scale-125 active:scale-95"
                      >
                        <Star className={`h-10 w-10 ${star <= newRating ? 'text-amber-400 fill-amber-400 drop-shadow-lg' : 'text-slate-300 hover:text-slate-400'}`} />
                      </button>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-black uppercase tracking-wider text-slate-700 mb-3">
                    {t.reviews?.commentLabel || "Votre commentaire"}
                  </label>
                  <textarea 
                    value={newComment} 
                    onChange={(e) => setNewComment(e.target.value)} 
                    className="w-full border-2 border-slate-200 rounded-2xl p-4 focus:ring-4 focus:ring-brand-200 focus:border-brand-600 transition-all font-medium text-slate-700 resize-none" 
                    rows="4" 
                    placeholder={t.reviews?.placeholder || "Racontez-nous votre expérience..."}
                  />
                </div>
                
                <button 
                  type="submit" 
                  disabled={submitting} 
                  className="group inline-flex items-center bg-brand-600 text-white px-10 py-4 rounded-full font-black text-lg hover:bg-brand-700 transition-all shadow-xl transform hover:-translate-y-1 hover:shadow-brand-500/40 border-b-4 border-brand-800 active:translate-y-0 active:border-b-0 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <Loader className="animate-spin h-5 w-5 mr-3" />
                  ) : (
                    <Send className="h-5 w-5 mr-3 group-hover:translate-x-1 transition-transform" />
                  )}
                  {submitting ? 'Publication...' : (t.reviews?.submit || "Publier mon avis")}
                </button>
              </form>
            ) : (
              <div className="text-center py-10 bg-gradient-to-br from-slate-50 to-brand-50 rounded-2xl border-2 border-dashed border-slate-300">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-full shadow-lg mb-4">
                  <LogIn className="h-8 w-8 text-brand-600" />
                </div>
                <p className="text-slate-600 mb-6 font-medium text-lg">
                  {t.reviews?.loginRequired || "Veuillez vous connecter pour laisser un avis."}
                </p>
                <Link 
                  to="/login" 
                  className="inline-flex items-center bg-brand-600 text-white px-8 py-3 rounded-full font-black hover:bg-brand-700 transition-all shadow-lg"
                >
                  <LogIn className="h-4 w-4 mr-2" />
                  {t.auth?.loginTitle || "Se connecter"}
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Liste des avis */}
        <div className="space-y-6">
          {loading ? (
            <div className="text-center py-16">
              <Loader className="animate-spin h-12 w-12 text-brand-600 mx-auto mb-4" />
              <p className="text-slate-500 font-medium">Chargement des avis...</p>
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center bg-gradient-to-br from-slate-50 to-brand-50 p-16 rounded-3xl border-2 border-dashed border-slate-300">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-full shadow-lg mb-6">
                <MessageSquare className="h-10 w-10 text-slate-400" />
              </div>
              <p className="text-slate-600 text-xl font-bold">
                {t.reviews?.empty || "Soyez le premier à laisser un avis !"}
              </p>
            </div>
          ) : (
            reviews.map((review, index) => (
              <div 
                key={review.id} 
                className="bg-white p-5 rounded-2xl shadow-md border border-slate-200 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 relative overflow-hidden group"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="absolute top-4 right-4 opacity-5 group-hover:opacity-10 transition-opacity">
                  <Quote className="h-16 w-16 text-slate-900" />
                </div>
                
                <div className="flex flex-col md:flex-row gap-4 relative z-10">
                  <div className="flex-shrink-0">
                    <div className="h-12 w-12 bg-gradient-to-br from-brand-500 to-brand-700 rounded-xl flex items-center justify-center text-white font-black text-lg uppercase shadow-md">
                      {review.firstname ? review.firstname.charAt(0) : 'C'}
                    </div>
                  </div>
                  
                  <div className="flex-grow">
                    <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-1 mb-3">
                      <div>
                        <h4 className="font-black text-base text-slate-900 mb-1">{review.firstname || 'Client'}</h4>
                        <div className="flex items-center gap-0.5">
                          {renderStars(review.rating)}
                        </div>
                      </div>
                      <span className="text-xs text-slate-400 font-medium">
                        {new Date(review.created_at).toLocaleDateString('fr-FR', { 
                          day: 'numeric', 
                          month: 'long', 
                          year: 'numeric' 
                        })}
                      </span>
                    </div>
                    
                    {review.comment && (
                      <div className="relative">
                        <p className="text-slate-600 text-sm leading-relaxed italic font-medium">
                          "{review.comment}"
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}